import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, OnDestroy } from "@angular/core";

type Theme = "light" | "dark";
const STORAGE_KEY = "crypto-explorer-theme";

@Injectable({ providedIn: "root" })
export class ThemeService implements OnDestroy {
  private readonly window: Window | null;
  private readonly systemTheme: MediaQueryList | undefined;
  private preference: Theme | null = null;
  private theme: Theme = "light";

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.window = document.defaultView;
    this.systemTheme = this.window?.matchMedia?.("(prefers-color-scheme: dark)");
    this.preference = this.readPreference();
    this.applyTheme();
    this.systemTheme?.addEventListener("change", this.onSystemChange);
    this.window?.addEventListener("storage", this.onStorageChange);
  }

  get isDark(): boolean {
    return this.theme === "dark";
  }

  get toggleLabel(): string {
    return this.isDark ? "فعال‌سازی حالت روشن" : "فعال‌سازی حالت تاریک";
  }

  toggle(): void {
    this.preference = this.isDark ? "light" : "dark";
    this.applyTheme();
    try {
      this.window?.localStorage.setItem(STORAGE_KEY, this.preference);
    } catch {
      // The current session can still change theme when storage is unavailable.
    }
  }

  ngOnDestroy(): void {
    this.systemTheme?.removeEventListener("change", this.onSystemChange);
    this.window?.removeEventListener("storage", this.onStorageChange);
  }

  private readonly onSystemChange = (): void => {
    if (!this.preference) this.applyTheme();
  };

  private readonly onStorageChange = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY || event.key === null) {
      this.preference = this.readPreference();
      this.applyTheme();
    }
  };

  private readPreference(): Theme | null {
    try {
      const value = this.window?.localStorage.getItem(STORAGE_KEY);
      return value === "dark" || value === "light" ? value : null;
    } catch {
      return null;
    }
  }

  private applyTheme(): void {
    this.theme = this.preference || (this.systemTheme?.matches ? "dark" : "light");
    this.document.documentElement.setAttribute("data-theme", this.theme);
    this.document.documentElement.style.colorScheme = this.theme;
  }
}
